import AccountBoxIcon from "@mui/icons-material/AccountBox";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Fab,
  Paper,
} from "@mui/material";
import { AnalyticsEvents, analytics } from "common/analytics";
import {
  DashboardIcon,
  FarmIcon,
  FilesIcon,
  InputsOutputsIcon,
  InsightsIcon,
  RecordsIcon,
} from "common/icons";
import { useTranslation } from "common/locales";
import { useIsMobile, useUserType } from "common/utils";
import { useOnlineStatus } from "common/utils/useOnlineStatus";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppStore } from "stores";
import RadialMenu from "./RadialMenu";
import { useAddNewEventDialogs } from "./useAddNewEventDialogs";

import { isFarmTabRoute, navigationConfig } from "app/routes";
import AccountTreeIconConfigurable from "common/icons/AccountTreeIconConfigurable";
import ProfileIcon from "common/icons/ProfileIcon";
import ProgrammesIcon from "common/icons/ProgrammesIcon";
import ProfileDialog from "features/userProfile/Profile";

/**
 * Bottom tab navigation component for mobile.
 * Shows different tabs based on user type (farmer vs corporate).
 */
const BottomTabNavigation = () => {
  const [isRadialMenuOpen, setIsRadialMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const userType = useUserType();
  const online = useOnlineStatus();
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const { handleEventTypeClick, dialogs, isAnyDialogOpen } =
    useAddNewEventDialogs();

  // Listen for EventFormDialog open/close (covers edit dialogs not tracked above)
  const [isEventFormDialogOpen, setIsEventFormDialogOpen] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => {
      const open = (e as CustomEvent<{ open: boolean }>).detail.open;
      setIsEventFormDialogOpen(open);
    };
    window.addEventListener("event-form-dialog", handler);
    return () => window.removeEventListener("event-form-dialog", handler);
  }, []);

  // Check if any dialog is open (add or edit)
  const isButtonDisabled = isAnyDialogOpen || isEventFormDialogOpen;

  // Check if on a public route (login, signup, etc.)
  const publicRoutes = [
    "/login",
    "/signup",
    "/signup-intro",
    "/login/password-forgot",
    "/login/password-reset",
  ];
  const isPublicRoute = publicRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(route + "/")
  );
  // Don't show on desktop, public routes, or if no current farm (for farmers)
  if (!isMobile || isPublicRoute || (userType === "farmer" && !currentFarmId)) {
    return null;
  }

  const handleAddClick = () => {
    if (isButtonDisabled) return;
    if (!isRadialMenuOpen) {
      analytics.track(AnalyticsEvents.ADD_BUTTON_CLICKED);
    }
    setIsRadialMenuOpen(!isRadialMenuOpen);
  };

  const handleRadialMenuClose = () => {
    setIsRadialMenuOpen(false);
  };

  const handleEventMenuItemClick = (eventType: string) => {
    handleRadialMenuClose();
    handleEventTypeClick(eventType);
  };

  // Icon mapping for navigation config
  const iconMap: Record<string, React.ReactNode> = {
    FarmIcon: <FarmIcon />,
    InputsOutputsIcon: <InputsOutputsIcon />,
    RecordsIcon: <RecordsIcon />,
    InsightsIcon: <InsightsIcon />,
    AddButtonIcon: null, // FAB is rendered separately
    ProgrammesIcon: <ProgrammesIcon />,
    DashboardIcon: <DashboardIcon />,
    AccountTreeIconConfigurable: <AccountTreeIconConfigurable />,
    FolderIcon: <FilesIcon />,
    AccountBoxIcon: <AccountBoxIcon />,
    ProfileIcon: <ProfileIcon />,
  };

  // Build tabs from navigation config
  const navConfig = navigationConfig[userType];
  const tabs = navConfig.tabs.map((tab) => ({
    id: tab.id,
    label: tab.isAddButton ? "" : t(tab.labelKey),
    icon: iconMap[tab.iconName] || null,
    value: tab.isAddButton ? "add" : tab.getPath(currentFarmId ?? undefined),
    analyticsEvent: tab.analyticsEvent,
    isAddButton: tab.isAddButton,
  }));

  const defaultTabValue = navConfig.getDefaultRoute(currentFarmId ?? undefined);

  // Get current value based on location
  const getCurrentValue = () => {
    // If any dialog is open, don't select any tab
    if (isButtonDisabled || isRadialMenuOpen) {
      return false;
    }

    const currentPath = location.pathname;

    // For farmer users, keep farm tab selected for farm-related views
    if (userType === "farmer" && isFarmTabRoute(currentPath)) {
      return `/farm/${currentFarmId}`;
    }

    return (
      tabs.find((tab) => tab.value === currentPath)?.value ?? defaultTabValue
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    handleRadialMenuClose();

    if (newValue === "add") {
      return; // Handled by the wrapper click
    }

    if (newValue === "/profile") {
      analytics.track(AnalyticsEvents.PROFILE_OPENED);
      setProfileDialogOpen(true);
      return;
    }

    const tab = tabs.find((tab) => tab.value === newValue);
    if (tab) {
      analytics.track(tab.analyticsEvent);
      navigate(newValue);
    }
  };

  // Check if this user type has an add button
  const hasAddButton = navConfig.tabs.some((tab) => tab.isAddButton);

  // Click handlers for split FABs - trigger Cancel/Save buttons
  const handleCancelClick = () => {
    // Find the Cancel/Back button — prefer data-action attribute, fall back to text content
    const cancelBtn = document.querySelector<HTMLButtonElement>(
      '[data-action="dialog-cancel"], [data-action="dialog-back"]'
    );
    if (cancelBtn) {
      cancelBtn.click();
      return;
    }
    const buttons = document.querySelectorAll("button");
    for (const btn of buttons) {
      if (btn.textContent?.trim() === "Cancel") {
        btn.click();
        return;
      }
    }
  };

  const handleSaveClick = () => {
    // Find the Save/Next button — prefer data-action attribute, fall back to text content
    const saveBtn = document.querySelector<HTMLButtonElement>(
      '[data-action="dialog-save"], [data-action="dialog-next"]'
    );
    if (saveBtn) {
      saveBtn.click();
      return;
    }
    const buttons = document.querySelectorAll("button");
    for (const btn of buttons) {
      if (btn.textContent?.trim() === "Save") {
        btn.click();
        return;
      }
    }
  };

  // Determine what icons to show for the FABs based on context
  const getLeftFabIcon = () => {
    if (document.querySelector('[data-action="dialog-back"]')) {
      return <ChevronLeftIcon sx={{ fontSize: 24 }} />;
    }
    return <CloseIcon sx={{ fontSize: 24 }} />;
  };

  const getRightFabIcon = () => {
    if (document.querySelector('[data-action="dialog-next"]')) {
      return <ChevronRightIcon sx={{ fontSize: 24 }} />;
    }
    return <CheckIcon sx={{ fontSize: 24 }} />;
  };

  return (
    <>
      {/* Floating Action Buttons */}
      {hasAddButton && (
        <Box
          sx={{
            position: "fixed",
            bottom: "calc(25px + env(safe-area-inset-bottom))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Cancel FAB (left) */}
          <Fab
            onClick={handleCancelClick}
            size="medium"
            sx={{
              position: "absolute",
              backgroundColor: "#D32F2F",
              color: "white",
              width: 48,
              height: 48,
              opacity: isButtonDisabled ? 1 : 0,
              transform: isButtonDisabled
                ? "translateX(-40px) scale(1)"
                : "translateX(0) scale(0)",
              transition: "transform 0.35s ease, opacity 0.35s ease",
              pointerEvents: isButtonDisabled ? "auto" : "none",
              "&:hover": {
                backgroundColor: "#B71C1C",
              },
            }}
          >
            {getLeftFabIcon()}
          </Fab>

          {/* Save FAB (right) */}
          <Fab
            onClick={handleSaveClick}
            size="medium"
            sx={{
              position: "absolute",
              backgroundColor: "#2E7D32",
              color: "white",
              width: 48,
              height: 48,
              opacity: isButtonDisabled ? 1 : 0,
              transform: isButtonDisabled
                ? "translateX(40px) scale(1)"
                : "translateX(0) scale(0)",
              transition: "transform 0.35s ease, opacity 0.35s ease",
              pointerEvents: isButtonDisabled ? "auto" : "none",
              "&:hover": {
                backgroundColor: "#1B5E20",
              },
            }}
          >
            {getRightFabIcon()}
          </Fab>

          {/* Main Add FAB (center) */}
          <Fab
            onClick={handleAddClick}
            disabled={isButtonDisabled}
            sx={{
              backgroundColor: isRadialMenuOpen ? "#2E7831" : "#1C4321",
              color: "white",
              width: 62,
              height: 62,
              opacity: isButtonDisabled ? 0 : 1,
              transform: isRadialMenuOpen
                ? "scale(1.05)"
                : isButtonDisabled
                ? "scale(0)"
                : "scale(1)",
              transition:
                "transform 0.35s ease, background-color 0.35s ease, opacity 0.35s ease",
              pointerEvents: isButtonDisabled ? "none" : "auto",
              "&:hover": {
                backgroundColor: isRadialMenuOpen ? "#2E7831" : "#153318",
              },
              "&.Mui-disabled": {
                backgroundColor: "#1C4321",
                color: "white",
                opacity: 0,
              },
            }}
          >
            <AddIcon
              sx={{
                fontSize: 32,
                transform: isRadialMenuOpen ? "rotate(135deg)" : "rotate(0deg)",
                transition: "transform 0.35s ease",
              }}
            />
          </Fab>
        </Box>
      )}
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          paddingBottom: "env(safe-area-inset-bottom)",
          backgroundColor: "white",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        }}
        elevation={8}
      >
        {!online && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 0.75,
              minHeight: 28,
              pl: 2,
              pr: hasAddButton ? 11 : 2,
              color: "#5B3D00",
              backgroundColor: "#FFF3CD",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              borderBottom: "1px solid rgba(91, 61, 0, 0.14)",
              fontSize: "0.75rem",
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            <WifiOffIcon sx={{ fontSize: "1rem" }} />
            Offline
          </Box>
        )}
        <BottomNavigation
          value={getCurrentValue()}
          onChange={handleChange}
          showLabels
          sx={{
            backgroundColor: "white",
            height: "auto",
            minHeight: "70px",
            borderTopLeftRadius: online ? "16px" : 0,
            borderTopRightRadius: online ? "16px" : 0,
            "& .MuiBottomNavigationAction-root": {
              color: "#9E9E9E",
              "&.Mui-selected": {
                color: "#1C4321",
                "& .MuiBottomNavigationAction-label": {
                  fontWeight: 600,
                },
                "& .MuiSvgIcon-root": {
                  strokeWidth: 2,
                },
              },
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.75rem",
              lineHeight: 1.2,
              textAlign: "center",
              whiteSpace: "normal",
              marginTop: "0.25rem",
              fontWeight: 400,
              "&.Mui-selected": {
                fontSize: "0.75rem",
              },
            },
          }}
        >
          {tabs.map((tab) =>
            tab.isAddButton ? (
              // Empty spacer for FAB position
              <BottomNavigationAction
                key={tab.id}
                label=""
                icon={null}
                value={tab.value}
                disabled
                sx={{
                  minWidth: 72,
                  padding: "8px 2px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            ) : (
              <BottomNavigationAction
                key={tab.id}
                label={tab.label}
                icon={tab.icon}
                value={tab.value}
                sx={{
                  minWidth: 0,
                  padding: "0.5rem 0.125rem",
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.65rem",
                    color: "inherit",
                  },
                }}
              />
            )
          )}
        </BottomNavigation>
      </Paper>
      <RadialMenu
        open={isRadialMenuOpen}
        onClose={handleRadialMenuClose}
        onEventTypeClick={handleEventMenuItemClick}
      />
      {dialogs}
      <ProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
    </>
  );
};

export default BottomTabNavigation;
